import { BaseModalRef } from '@/app/helpers/types';
import { useOpen } from '@/app/hooks';
import { Box, Button, Dialog, Stack, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle, useState } from 'react';
import ModalTemplate from '../modal-template';
import { useTranslation } from 'react-i18next';
import IndustryIcon from '@/app/assets/icons/industry-windows.svg?react';
import ShopIcon from '@/app/assets/icons/shop.svg?react';
import { Link } from 'react-router-dom';
import UISwitch from '@/shared/ui/ui-switch';

const NewRequestModal = forwardRef<BaseModalRef>((_props, ref) => {
  const { t } = useTranslation();
  const { open, setOpen } = useOpen();
  const [isInternationalFactoring, setIsInternationalFactoring] =
    useState<boolean>(false);

  useImperativeHandle(
    ref,
    () => ({
      open: handleOpen,
      close: handleClose,
    }),
    []
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const blocks = [
    {
      icon: <IndustryIcon />,
      title: t('im_supplier'),
      description: t('im_supplier_description'),
      link: isInternationalFactoring
        ? '/seller/deals/create?isInternationalFactoring=true'
        : '/seller/deals/create',
    },
    {
      icon: <ShopIcon />,
      title: t('im_buyer'),
      description: t('im_buyer_description'),
      link: isInternationalFactoring
        ? '/buyer/deals/create?isInternationalFactoring=true'
        : '/buyer/deals/create',
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          maxWidth: 'fit-content',
        },
      }}
    >
      <ModalTemplate title={t('new_bid')} onClose={handleClose} noBtns>
        <Stack direction='column'>
          <Stack direction='row' alignItems='center' spacing={1.2} mb={2.5}>
            <UISwitch
              checked={isInternationalFactoring}
              onChange={() =>
                setIsInternationalFactoring(!isInternationalFactoring)
              }
            />
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {t('international_factoring')}
            </Typography>
          </Stack>

          <Stack
            spacing={2}
            columnGap={2}
            useFlexGap
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            {blocks.map((block, index) => (
              <Box
                key={index}
                p={3}
                sx={{
                  backgroundColor: 'rgba(21, 85, 252, 0.04)',
                  borderRadius: '16px',
                  maxWidth: 320,
                  width: '100%',
                }}
              >
                {block.icon}
                <Typography variant='h6' sx={{ mt: 3, mb: 1 }}>
                  {block.title}
                </Typography>
                <Typography variant='body2' height={40}>
                  {block.description}
                </Typography>
                <Link to={block.link} onClick={handleClose}>
                  <Button sx={{ mt: 3 }}>{t('add_bid')}</Button>
                </Link>
              </Box>
            ))}
          </Stack>
        </Stack>
      </ModalTemplate>
    </Dialog>
  );
});

export default NewRequestModal;
